import React, { useState } from 'react';
import { ReconciliationSummary } from '../../types/reconciliation';

interface StatusDistributionChartProps {
  summary: ReconciliationSummary;
  onSelectStatus?: (status: string) => void;
}

export const StatusDistributionChart: React.FC<StatusDistributionChartProps> = ({
  summary,
  onSelectStatus,
}) => {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  const categories = [
    { label: 'Exact Match', count: summary.exactMatches, color: '#10b981', textColor: 'text-emerald-500' },
    { label: 'Partial Match', count: summary.partialMatches, color: '#f59e0b', textColor: 'text-amber-500' },
    { label: 'Over Billed', count: summary.overBilled, color: '#8b5cf6', textColor: 'text-purple-500' },
    { label: 'No Bill Reference', count: summary.noBillReference, color: '#ef4444', textColor: 'text-rose-500' },
    { label: 'ERP Only', count: summary.erpOnly, color: '#6366f1', textColor: 'text-indigo-500' },
    { label: 'Refunded', count: summary.refundedCount, color: '#0ea5e9', textColor: 'text-sky-500' },
  ].filter((c) => c.count > 0);

  const total = summary.totalGroups || 1;

  // Compute SVG Donut Chart Paths
  let cumulativeAngle = 0;
  const size = 200;
  const center = size / 2;
  const radius = 75;
  const strokeWidth = 26;

  const slices = categories.map((cat, idx) => {
    const fraction = cat.count / total;
    const angle = fraction * 360;
    const startAngle = cumulativeAngle;
    const endAngle = cumulativeAngle + angle;
    cumulativeAngle += angle;

    const startRad = ((startAngle - 90) * Math.PI) / 180;
    const endRad = ((endAngle - 90) * Math.PI) / 180;

    const x1 = center + radius * Math.cos(startRad);
    const y1 = center + radius * Math.sin(startRad);
    const x2 = center + radius * Math.cos(endRad);
    const y2 = center + radius * Math.sin(endRad);

    const largeArcFlag = angle > 180 ? 1 : 0;

    // SVG arc path
    const pathData =
      cat.count === total
        ? `M ${center} ${center - radius} A ${radius} ${radius} 0 1 1 ${center - 0.001} ${center - radius}`
        : `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`;

    return {
      ...cat,
      percentage: Math.round(fraction * 1000) / 10,
      pathData,
      idx,
    };
  });

  return (
    <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col justify-between">
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-slate-900 dark:text-white text-sm">
              Reconciliation Distribution
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Categorized by reconciliation rules
            </p>
          </div>
          <span className="text-xs font-mono font-semibold px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
            {summary.totalGroups} Groups
          </span>
        </div>

        {/* Donut Graphic with center text */}
        <div className="relative flex items-center justify-center my-2">
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="transform -rotate-90">
            {slices.map((slice) => (
              <path
                key={slice.label}
                d={slice.pathData}
                fill="none"
                stroke={slice.color}
                strokeWidth={hoveredIdx === slice.idx ? strokeWidth + 4 : strokeWidth}
                strokeLinecap="round"
                className="transition-all duration-200 cursor-pointer"
                onMouseEnter={() => setHoveredIdx(slice.idx)}
                onMouseLeave={() => setHoveredIdx(null)}
                onClick={() => onSelectStatus && onSelectStatus(slice.label)}
              />
            ))}
          </svg>

          {/* Center text */}
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">
              {hoveredIdx !== null ? `${slices[hoveredIdx].percentage}%` : `${summary.matchPercentage}%`}
            </span>
            <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400">
              {hoveredIdx !== null ? slices[hoveredIdx].label : 'Match Rate'}
            </span>
          </div>
        </div>
      </div>

      {/* Legend list */}
      <div className="grid grid-cols-2 gap-2 pt-3 border-t border-slate-100 dark:border-slate-800 mt-2">
        {slices.map((cat) => (
          <button
            key={cat.label}
            onClick={() => onSelectStatus && onSelectStatus(cat.label)}
            onMouseEnter={() => setHoveredIdx(cat.idx)}
            onMouseLeave={() => setHoveredIdx(null)}
            className="flex items-center justify-between text-left p-1.5 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/60 transition group"
          >
            <div className="flex items-center gap-2 truncate">
              <span
                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: cat.color }}
              />
              <span className="text-xs text-slate-600 dark:text-slate-300 truncate group-hover:text-slate-900 dark:group-hover:text-white font-medium">
                {cat.label}
              </span>
            </div>
            <span className="text-xs font-mono font-semibold text-slate-700 dark:text-slate-300 ml-1">
              {cat.count}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};
